import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import EyeMark from "@/components/EyeMark";

const links = [
    { to: "/", label: "Home", testid: "nav-link-home" },
    { to: "/about", label: "About", testid: "nav-link-about" },
    { to: "/services", label: "Services", testid: "nav-link-services" },
    { to: "/industries", label: "Industries", testid: "nav-link-industries" },
    { to: "/contact", label: "Contact", testid: "nav-link-contact" },
];

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setOpen(false), [pathname]);

    return (
        <header
            data-testid="main-navigation"
            className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow] duration-300 bg-[#0C0C0C]/85 backdrop-blur-md border-b border-white/10 ${
                scrolled ? "shadow-[0_8px_30px_rgba(0,0,0,0.35)]" : ""
            }`}
        >
            <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
                <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
                    <EyeMark size={38} />
                    <span className="leading-tight">
                        <span className="block font-display font-semibold text-[#F6F4EF] text-[15px] tracking-wide">
                            Vantage Crest Research
                        </span>
                        <span className="block font-mono-accent text-[9px] tracking-[0.22em] text-[#A6A69C] uppercase">
                            Talent Intelligence, Delivered.
                        </span>
                    </span>
                </Link>

                <nav className="hidden lg:flex items-center gap-8">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            data-testid={l.testid}
                            className={({ isActive }) =>
                                `relative text-sm tracking-wide transition-colors duration-200 ${
                                    isActive
                                        ? "text-[#F6F4EF]"
                                        : "text-[#A6A69C] hover:text-[#E7E4DC]"
                                } after:absolute after:-bottom-2 after:left-0 after:h-px after:bg-[#C9A24B] after:transition-[width] after:duration-300 ${
                                    isActive ? "after:w-full" : "after:w-0 hover:after:w-full"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                    <Link
                        to="/contact"
                        data-testid="nav-btn-book-consultation"
                        className="ml-2 inline-flex items-center gap-1.5 bg-[#C9A24B] hover:bg-[#D8BC7A] text-[#0C0C0C] text-sm font-semibold px-5 py-2.5 transition-colors duration-200 active:scale-[0.98]"
                    >
                        Book a Consultation
                        <ArrowUpRight size={15} />
                    </Link>
                </nav>

                <button
                    className="lg:hidden text-[#E7E4DC] p-2"
                    onClick={() => setOpen(!open)}
                    aria-label="Toggle menu"
                    data-testid="nav-mobile-toggle"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                        className="lg:hidden overflow-hidden bg-[#0C0C0C]/97 backdrop-blur-xl border-t border-white/10"
                        data-testid="nav-mobile-drawer"
                    >
                        <div className="px-6 py-6 flex flex-col gap-1">
                            {links.map((l, i) => (
                                <motion.div
                                    key={l.to}
                                    initial={{ opacity: 0, x: -12 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.05 * i }}
                                >
                                    <NavLink
                                        to={l.to}
                                        end={l.to === "/"}
                                        data-testid={`${l.testid}-mobile`}
                                        className={({ isActive }) =>
                                            `block py-3 text-lg border-b border-white/5 ${
                                                isActive ? "text-[#D8BC7A]" : "text-[#E2E2DB]"
                                            }`
                                        }
                                    >
                                        {l.label}
                                    </NavLink>
                                </motion.div>
                            ))}
                            <Link
                                to="/contact"
                                data-testid="nav-btn-book-consultation-mobile"
                                className="mt-4 inline-flex items-center justify-center gap-2 bg-[#C9A24B] text-[#0C0C0C] font-semibold px-5 py-3.5"
                            >
                                Book a Consultation <ArrowUpRight size={16} />
                            </Link>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
}
